import { createFlagsmithInstance } from "flagsmith";
import { useEffect, useState } from "react";
import { useProfile } from "../providers/profileProvider/ProfileProvider";

/** Flags ops flip at runtime that open sessions must pick up without a reload. */
export const LIVE_FLAGS = ["user_notice", "system_maintenance"] as const;
export type LiveFlagName = (typeof LIVE_FLAGS)[number];

export const LIVE_FLAGS_POLL_INTERVAL_MS = 1 * 60 * 1000;

export type LiveFlag = {
  enabled: boolean;
  value: unknown;
};

export type LiveFlags = Record<LiveFlagName, LiveFlag>;

const sameFlag = (a: LiveFlag, b: LiveFlag) =>
  a.enabled === b.enabled && a.value === b.value;

const sameFlags = (a: LiveFlags, b: LiveFlags) =>
  LIVE_FLAGS.every(name => sameFlag(a[name], b[name]));

/**
 * Live view of the LIVE_FLAGS for sessions that are already open when ops
 * flip one of them.
 *
 * The app's Flagsmith client fetches once per page load and caches for an
 * hour, and Flagsmith cannot poll or push a single flag: any refresh replaces
 * the whole flag set and re-renders every `useFlags` consumer. So these flags
 * get their own client instance, with the shared cache disabled, and poll on
 * their own. Nothing else in the app reads them, so the rest keep their
 * per-page-load behaviour. Cost: one small request per interval per open tab.
 *
 * Call it once (MaintenanceGate does); every call is its own poller.
 */
export const useLiveFlags = (): LiveFlags | null => {
  const { profile } = useProfile();
  const identity = profile?.sub;
  const [flags, setFlags] = useState<LiveFlags | null>(null);

  useEffect(() => {
    const environmentID = process.env.NEXT_PUBLIC_FLAGSMITH_CLIENT_API_KEY;
    if (!environmentID || !identity) return;

    const instance = createFlagsmithInstance();
    let cancelled = false;

    const publish = () => {
      if (cancelled) return;
      const next = Object.fromEntries(
        LIVE_FLAGS.map(name => [
          name,
          { enabled: instance.hasFeature(name), value: instance.getValue(name) }
        ])
      ) as LiveFlags;
      // Only re-render when a flag actually changed: `onChange` fires on
      // every poll.
      setFlags(prev => (prev && sameFlags(prev, next) ? prev : next));
    };

    instance
      .init({
        environmentID,
        identity,
        api: process.env.NEXT_PUBLIC_FLAGSMITH_API_URL,
        cacheFlags: false,
        enableAnalytics: false,
        onChange: publish,
        onError: () => {
          // A failed poll keeps the last known state; the next one retries.
        }
      })
      .catch(() => {
        // Same as onError: keep polling, a later fetch may succeed.
      })
      .finally(() => {
        if (!cancelled) instance.startListening(LIVE_FLAGS_POLL_INTERVAL_MS);
      });

    return () => {
      cancelled = true;
      instance.stopListening();
    };
  }, [identity]);

  return flags;
};
